import {
  fallbackHexForBrandEn,
  logoUrlForBrandEn,
} from "@/components/car-listing/brand-logos";
import { cn } from "@/lib/utils";

export function vehicleOptionTextValue(nameAr: string, nameEn: string): string {
  const arabic = nameAr.trim();
  const english = nameEn.trim();
  if (!english || english.toLowerCase() === arabic.toLowerCase()) return arabic;
  return `${arabic} ${english}`;
}

export function matchesVehicleOptionQuery(nameAr: string, nameEn: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    nameAr.toLowerCase().includes(q) ||
    nameEn.toLowerCase().includes(q) ||
    vehicleOptionTextValue(nameAr, nameEn).toLowerCase().includes(q)
  );
}

function BilingualNames({ nameAr, nameEn }: { nameAr: string; nameEn: string }) {
  const arabic = nameAr.trim() || nameEn.trim();
  const english = nameEn.trim();
  const showEnglish = Boolean(english && english.toLowerCase() !== arabic.toLowerCase());

  return (
    <>
      <span className="min-w-0 truncate font-medium">{arabic}</span>
      {showEnglish ? (
        <span className="min-w-0 truncate text-sm opacity-80" dir="ltr">
          {english}
        </span>
      ) : null}
    </>
  );
}

function MakeLogo({
  nameAr,
  nameEn,
  logo,
}: {
  nameAr: string;
  nameEn: string;
  logo?: string | null;
}) {
  const src = logoUrlForBrandEn(nameEn, logo);
  const label = nameAr.trim() || nameEn.trim();
  const initial = label.charAt(0);
  const tileClass =
    "flex size-9 shrink-0 items-center justify-center rounded-md bg-gray-50/90 p-1 shadow-sm ring-1 ring-black/10";

  if (!src) {
    return (
      <span
        aria-hidden
        className={cn(tileClass, "text-xs font-bold text-white")}
        style={{ backgroundColor: fallbackHexForBrandEn(nameEn) }}
      >
        {initial}
      </span>
    );
  }

  return (
    <span className={tileClass}>
      <img
        src={src}
        alt=""
        width={32}
        height={32}
        className="size-7 object-contain drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
      />
    </span>
  );
}

export function MakeSelectOption({
  nameAr,
  nameEn,
  logo,
  className,
}: {
  nameAr: string;
  nameEn: string;
  logo?: string | null;
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 w-full items-center gap-2", className)}>
      <MakeLogo nameAr={nameAr} nameEn={nameEn} logo={logo} />
      <BilingualNames nameAr={nameAr} nameEn={nameEn} />
    </span>
  );
}

export function ModelSelectOption({
  nameAr,
  nameEn,
  className,
}: {
  nameAr: string;
  nameEn: string;
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 w-full items-center gap-2", className)}>
      <BilingualNames nameAr={nameAr} nameEn={nameEn} />
    </span>
  );
}

export function BodySelectOption({
  nameAr,
  nameEn,
  image,
  imageWhiteBg = false,
  imageMirror = false,
  className,
}: {
  nameAr: string;
  nameEn: string;
  image: string;
  imageWhiteBg?: boolean;
  imageMirror?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 w-full items-center gap-2", className)}>
      <span className="flex h-8 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-black/10">
        <img
          src={image}
          alt=""
          width={48}
          height={32}
          className={cn(
            "max-h-7 max-w-11 object-contain",
            imageWhiteBg && "mix-blend-multiply",
            imageMirror && "scale-x-[-1]",
          )}
          loading="lazy"
          decoding="async"
        />
      </span>
      <BilingualNames nameAr={nameAr} nameEn={nameEn} />
    </span>
  );
}
